import { IDimension, IPageDimension } from '../interfaces/IConnectedPagesGraphProperties';
import { calculatePointOnCircle } from './MathUtils';
import { GroupedAssignedPage, IConnectedPage } from '../../../../../types/api/connectedPages/IConnectedPage';

const getWidthFromPercentage = (containerWidth: number, percentage: number, width?: number) => {
  if (typeof width === 'number') {
    return (width / 100) * percentage;
  }

  return (containerWidth / 100) * percentage;
};

export const getCurrentWidth = (containerWidth: number, pageDimension: IDimension, baseWidth?: number) => {
  let currentWidth = 0;
  if (pageDimension.percentage) {
    currentWidth = getWidthFromPercentage(containerWidth, pageDimension.percentage, baseWidth);
  }

  if (currentWidth < pageDimension.minWidth) {
    return pageDimension.minWidth;
  }

  if (currentWidth > pageDimension.maxWidth) {
    return pageDimension.maxWidth;
  }

  return currentWidth;
};

export const getCharacterCount = (containerWidth: number, pageDimension: IPageDimension): number => {
  const currentWithAsPercentFromMaximalWidth = getCurrentWidth(containerWidth, pageDimension) / pageDimension.maxWidth;
  const currentCharacterCount = pageDimension.maxCharacters * currentWithAsPercentFromMaximalWidth;

  if (currentCharacterCount > pageDimension.maxCharacters && currentCharacterCount <= pageDimension.maxCharacters) {
    return currentCharacterCount;
  }
  if (currentCharacterCount > pageDimension.minCharacters) {
    return pageDimension.maxCharacters;
  }

  return pageDimension.minCharacters;
};

export const getContainerWidth = (container: HTMLElement | undefined): number => {
  return container ? container.getBoundingClientRect().width : 0;
};

export const getContainerHeight = (container: HTMLElement | undefined): number => {
  return container ? container.getBoundingClientRect().height : 0;
};

export const getChildPagesCoordinates = (
  containerDimensions: { width: number; height: number },
  index: number,
  maxPagesPerRow: number,
  radius = 0,
  row = 1
) => {
  const center = { x: 25, y: 25 };

  if (containerDimensions.width > 0) {
    center.x = containerDimensions.width / 2;
    // if no bounding rectangle height, assume square dimension and assign width
    center.y = containerDimensions.height > 0 ? containerDimensions.height / 2 : containerDimensions.width / 2;
  }

  return calculatePointOnCircle((360 / (maxPagesPerRow + 1)) * index - 90, center, radius, row);
};

export const getChildPages = (
  currentPage: IConnectedPage,
  currentSet: number,
  maxPagesPerRow: number
): IConnectedPage[] => {
  if (currentPage.childPages) {
    return currentPage.childPages.slice(maxPagesPerRow * currentSet, maxPagesPerRow * (currentSet + 1));
  }

  return [];
};

export const getGroupedAssignedPages = (
  currentSet: number,
  groupedAssignedPages: GroupedAssignedPage[],
  maxPagesPerRow: number,
  activeCustomActionButton: string
): IConnectedPage[] => {
  const active = groupedAssignedPages.find(page => page.roleId === activeCustomActionButton);

  if (active) {
    return active.pages.slice(maxPagesPerRow * currentSet, maxPagesPerRow * (currentSet + 1));
  }
  return [];
};

export const getButtonCoordinates = (
  containerDimensions: { width: number; height: number },
  maxPagesPerRow: number,
  radiusOfFirstRow: number
) => {
  // This indicates a margin between the pagination button and it's neighbor page element
  const gapBetweenButtonAndPage = 0.7;
  const coordinatesNextButton = getChildPagesCoordinates(
    containerDimensions,
    (2 - gapBetweenButtonAndPage) * -1,
    maxPagesPerRow,
    radiusOfFirstRow
  );

  const coordinatesPreviousButton = getChildPagesCoordinates(
    containerDimensions,
    gapBetweenButtonAndPage * -1,
    maxPagesPerRow,
    radiusOfFirstRow
  );

  return { coordinatesNextButton, coordinatesPreviousButton };
};
